// orchestrator_fullstack/frontend/src/app/admin/ingress/TracePanel.tsx
import React, { useEffect, useMemo, useState } from "react";
import AdminTraceLink from "./AdminTraceLink";
import { apiJson } from "../../ingress/api";
import { getTenantUuid, getTenantLabel } from "../../ingress/tenant";

type TraceSummaryResp = {
  traceId: string;
  summary: {
    spanCount: number;
    errorCount: number;
    durationMs: number;
    startTimeUnixMs: number;
    endTimeUnixMs: number;
    providers: Array<{ name: string; spans: number }>;
    nodes: Array<{ node_id: string; spans: number }>;
  };
};

type TraceDetailResp = {
  traceId: string;
  startTimeUnixMs: number;
  endTimeUnixMs: number;
  durationMs: number;
  spans: Array<{
    spanId: string;
    parentSpanId?: string | null;
    name: string;
    startTimeUnixMs: number;
    endTimeUnixMs: number;
    durationMs: number;
    status: "ok" | "error";
    attributes: Record<string, any>;
  }>;
};

function ms(n: number) {
  if (!Number.isFinite(n)) return "—";
  if (n < 1000) return `${n}ms`;
  return `${(n / 1000).toFixed(2)}s`;
}

function getEnv(name: string, fallback: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const v = (import.meta as any)?.env?.[name];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = (window as any)?.__ENV__?.[name];
  return (v || w || fallback) as string;
}

function buildGrafanaExploreUrl(traceId: string) {
  const base = getEnv("VITE_GRAFANA_BASE_URL", "http://localhost:3000").replace(/\/+$/, "");

  const tempoUid = getEnv("VITE_GRAFANA_TEMPO_UID", "tempo");

  const orgIdRaw = getEnv("VITE_GRAFANA_ORG_ID", "1");
  const orgId = /^\d+$/.test(orgIdRaw) ? orgIdRaw : "1";
  const from = getEnv("VITE_GRAFANA_TRACE_RANGE_FROM", "now-1h");
  const to = getEnv("VITE_GRAFANA_TRACE_RANGE_TO", "now");

  const panes: Record<string, any> = {
    y9p: {
      datasource: tempoUid,
      queries: [
        {
          refId: "A",
          datasource: { type: "tempo", uid: tempoUid },
          queryType: "traceql",
          limit: 20,
          tableType: "traces",
          query: traceId,
        },
      ],
      range: { from, to },
    },
  };

  const panesEnc = encodeURIComponent(JSON.stringify(panes));
  return `${base}/explore?schemaVersion=1&panes=${panesEnc}&orgId=${encodeURIComponent(orgId)}`;
}

export default function TracePanel(props: {
  traceId?: string | null;
  ingressRequestId?: string | null;
  runId?: string | null;
  className?: string;
}) {
  const tenantUuid = getTenantUuid();
  const tenantLabel = getTenantLabel();

  const traceId = (props.traceId || "").trim();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"summary" | "embedded" | "raw">("summary");

  const [summary, setSummary] = useState<TraceSummaryResp | null>(null);
  const [summaryErr, setSummaryErr] = useState<string | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [trace, setTrace] = useState<TraceDetailResp | null>(null);
  const [traceErr, setTraceErr] = useState<string | null>(null);
  const [traceLoading, setTraceLoading] = useState(false);

  const iframeSrc = useMemo(() => (traceId ? buildGrafanaExploreUrl(traceId) : ""), [traceId]);

  async function loadSummary() {
    if (!traceId) return;
    setSummaryLoading(true);
    setSummaryErr(null);
    try {
      const d = await apiJson<TraceSummaryResp>(
        `/v1/admin/observability/trace/${encodeURIComponent(traceId)}/summary?tenant_id=${tenantUuid}`,
        { tenantLabel }
      );
      setSummary(d);
    } catch (e: any) {
      setSummary(null);
      setSummaryErr(e?.message || String(e));
    } finally {
      setSummaryLoading(false);
    }
  }

  async function loadTrace() {
    if (!traceId) return;
    setTraceLoading(true);
    setTraceErr(null);
    try {
      const d = await apiJson<TraceDetailResp>(
        `/v1/admin/observability/trace/${encodeURIComponent(traceId)}?tenant_id=${tenantUuid}`,
        { tenantLabel }
      );
      setTrace(d);
    } catch (e: any) {
      setTrace(null);
      setTraceErr(e?.message || String(e));
    } finally {
      setTraceLoading(false);
    }
  }

  useEffect(() => {
    // prefetch summary when panel is opened
    if (open && mode === "summary" && traceId && !summary && !summaryLoading) {
      loadSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, traceId]);

  if (!traceId) {
    return (
      <div className={props.className}>
        <div className="text-xs text-gray-500">Trace</div>
        <div className="text-sm text-gray-700">—</div>
        <div className="text-[11px] text-gray-500 mt-1">
          Trace not available for this request yet (or request created before trace capture was enabled).
        </div>
      </div>
    );
  }

  return (
    <div className={props.className}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs text-gray-500">Trace</div>
          <div className="mt-1">
            <AdminTraceLink traceId={traceId} />
          </div>
        </div>

        <button
          className="px-3 py-2 rounded border text-sm hover:bg-gray-50"
          type="button"
          onClick={() => setOpen((v) => !v)}
          title="Toggle embedded trace panel"
        >
          {open ? "Hide" : "Show"}
        </button>
      </div>

      {open && (
        <div className="mt-3 border rounded bg-white">
          <div className="px-3 py-2 border-b flex items-center gap-2 flex-wrap">
            <button
              className={`px-2 py-1 rounded border text-xs ${mode === "summary" ? "bg-gray-900 text-white border-gray-900" : "hover:bg-gray-50"}`}
              type="button"
              onClick={() => setMode("summary")}
            >
              Summary
            </button>
            <button
              className={`px-2 py-1 rounded border text-xs ${mode === "embedded" ? "bg-gray-900 text-white border-gray-900" : "hover:bg-gray-50"}`}
              type="button"
              onClick={() => setMode("embedded")}
            >
              Embedded Grafana
            </button>
            <button
              className={`px-2 py-1 rounded border text-xs ${mode === "raw" ? "bg-gray-900 text-white border-gray-900" : "hover:bg-gray-50"}`}
              type="button"
              onClick={() => {
                setMode("raw");
                if (!trace && !traceLoading) loadTrace();
              }}
            >
              Raw
            </button>

            <div className="ml-auto flex items-center gap-2">
              {mode === "summary" && (
                <button
                  className="px-2 py-1 rounded border text-xs hover:bg-gray-50 disabled:opacity-60"
                  type="button"
                  onClick={loadSummary}
                  disabled={summaryLoading}
                >
                  {summaryLoading ? "Refreshing…" : "Refresh summary"}
                </button>
              )}
              {mode === "raw" && (
                <button
                  className="px-2 py-1 rounded border text-xs hover:bg-gray-50 disabled:opacity-60"
                  type="button"
                  onClick={loadTrace}
                  disabled={traceLoading}
                >
                  {traceLoading ? "Refreshing…" : "Refresh trace"}
                </button>
              )}
            </div>
          </div>

          {mode === "summary" && (
            <div className="p-3">
              {summaryErr && <div className="text-sm text-red-700 whitespace-pre-wrap">{summaryErr}</div>}
              {!summary && !summaryLoading ? (
                <div className="text-sm text-gray-600">No summary loaded yet.</div>
              ) : summaryLoading && !summary ? (
                <div className="text-sm text-gray-600">Loading trace summary…</div>
              ) : summary ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                  <div className="border rounded p-3">
                    <div className="text-xs text-gray-500">Duration</div>
                    <div className="mt-1 text-sm font-medium">{ms(summary.summary.durationMs)}</div>
                    <div className="mt-2 text-xs text-gray-600">
                      spans: <b>{summary.summary.spanCount}</b> · errors:{" "}
                      <b className={summary.summary.errorCount ? "text-red-700" : ""}>{summary.summary.errorCount}</b>
                    </div>
                  </div>

                  <div className="border rounded p-3">
                    <div className="text-xs text-gray-500">Providers</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {summary.summary.providers.length ? (
                        summary.summary.providers.slice(0, 8).map((p) => (
                          <span key={p.name} className="text-xs px-2 py-1 rounded border bg-gray-50">
                            {p.name} <span className="text-gray-500">({p.spans})</span>
                          </span>
                        ))
                      ) : (
                        <div className="text-sm text-gray-600">—</div>
                      )}
                    </div>
                  </div>

                  <div className="border rounded p-3">
                    <div className="text-xs text-gray-500">Top nodes</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {summary.summary.nodes.length ? (
                        summary.summary.nodes.slice(0, 8).map((n) => (
                          <span key={n.node_id} className="text-xs px-2 py-1 rounded border bg-gray-50 font-mono">
                            {n.node_id} <span className="text-gray-500">({n.spans})</span>
                          </span>
                        ))
                      ) : (
                        <div className="text-sm text-gray-600">—</div>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {mode === "embedded" && (
            <div className="p-3">
              <div className="text-xs text-gray-500 mb-2">
                Embedded Grafana Explore (Tempo). If it doesn’t load, open in new tab (button above).
              </div>
              <iframe
                title="Grafana Trace Explore"
                src={iframeSrc}
                className="w-full border rounded"
                style={{ height: 720 }}
              />
              <div className="text-[11px] text-gray-500 mt-2">
                For SSO environments, Grafana must allow embedding and same-site cookies must permit iframe sessions.
              </div>
            </div>
          )}

          {mode === "raw" && (
            <div className="p-3">
              {traceErr && <div className="text-sm text-red-700 whitespace-pre-wrap">{traceErr}</div>}
              {!trace && traceLoading ? (
                <div className="text-sm text-gray-600">Loading trace…</div>
              ) : !trace ? (
                <div className="text-sm text-gray-600">No trace loaded yet.</div>
              ) : (
                <pre className="text-xs bg-gray-50 border rounded p-3 overflow-auto max-h-[720px]">
                  {JSON.stringify(trace, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}